import React, { useEffect, useState } from 'react';
import { transferAPI, employeesAPI, companiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowRightLeft, AlertTriangle } from 'lucide-react';

const TransferEmployeeDialog = ({ open, onOpenChange, employee, onSuccess }) => {
  const [companies, setCompanies] = useState([]);
  const [managers, setManagers] = useState([]);
  const [form, setForm] = useState({
    target_company_id: '', effective_date: new Date().toISOString().split('T')[0],
    new_designation: '', new_department: '', new_basic_salary: '', new_currency: '',
    new_manager_id: '', new_branch_location: '',
    retain_leave_balance: true, reason: '', notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [targetCompany, setTargetCompany] = useState(null);

  useEffect(() => {
    if (open) {
      companiesAPI.getAll().then(r => setCompanies(r.data.filter(c => c.id !== employee?.company_id)));
      employeesAPI.getAll().then(r => setManagers(r.data.filter(e => e.id !== employee?.id && ['manager', 'hr_admin', 'super_admin'].includes(e.role || ''))));
      setForm({
        target_company_id: '', effective_date: new Date().toISOString().split('T')[0],
        new_designation: employee?.designation || '', new_department: employee?.department || '',
        new_basic_salary: '', new_currency: '', new_manager_id: '', new_branch_location: '',
        retain_leave_balance: true, reason: '', notes: ''
      });
      setTargetCompany(null);
    }
  }, [open, employee]);

  const onTargetChange = (id) => {
    setForm({ ...form, target_company_id: id });
    const c = companies.find(x => x.id === id);
    setTargetCompany(c);
    if (c) setForm(prev => ({ ...prev, target_company_id: id, new_currency: c.currency || '' }));
  };

  const submit = async () => {
    if (!form.target_company_id || !form.effective_date || !form.reason) {
      toast.error('Target company, effective date and reason are required'); return;
    }
    if (!window.confirm(`Transfer ${employee.first_name} ${employee.last_name} to ${targetCompany?.name}? This is recorded permanently in their history.`)) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (payload.new_basic_salary === '' || payload.new_basic_salary === null) delete payload.new_basic_salary;
      else payload.new_basic_salary = parseFloat(payload.new_basic_salary);
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === null) delete payload[k]; });
      payload.target_company_id = form.target_company_id;
      payload.effective_date = form.effective_date;
      payload.reason = form.reason;
      payload.retain_leave_balance = form.retain_leave_balance;
      await transferAPI.transfer(employee.id, payload);
      toast.success('Employee transferred successfully');
      onOpenChange(false);
      onSuccess && onSuccess();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
    finally { setSubmitting(false); }
  };

  if (!employee) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ArrowRightLeft size={18} />Inter-Company Transfer — {employee.first_name} {employee.last_name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-900/50">
            <CardContent className="p-3 text-sm flex items-start gap-2">
              <AlertTriangle size={16} className="mt-0.5 text-amber-600" />
              <span className="text-amber-700 dark:text-amber-300">This will move the employee to a different company. Their employee ID and history are preserved, but payroll cuts over to the new entity from the effective date.</span>
            </CardContent>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Target Company *</Label>
              <Select value={form.target_company_id} onValueChange={onTargetChange}>
                <SelectTrigger data-testid="transfer-target-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name} ({c.country})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Effective Date *</Label>
              <Input type="date" value={form.effective_date} onChange={(e) => setForm({...form, effective_date: e.target.value})} data-testid="transfer-effective-date" />
            </div>
            <div>
              <Label>New Designation</Label>
              <Input value={form.new_designation} onChange={(e) => setForm({...form, new_designation: e.target.value})} data-testid="transfer-designation" />
            </div>
            <div>
              <Label>New Department</Label>
              <Input value={form.new_department} onChange={(e) => setForm({...form, new_department: e.target.value})} data-testid="transfer-department" />
            </div>
            <div>
              <Label>New Basic Salary (in target currency)</Label>
              <Input type="number" value={form.new_basic_salary} onChange={(e) => setForm({...form, new_basic_salary: e.target.value})} placeholder="Leave blank to keep current" data-testid="transfer-salary" />
            </div>
            <div>
              <Label>New Currency</Label>
              <Input value={form.new_currency} onChange={(e) => setForm({...form, new_currency: e.target.value})} placeholder={targetCompany?.currency || 'e.g. AED, INR'} data-testid="transfer-currency" />
            </div>
            <div>
              <Label>New Branch Location</Label>
              <Input value={form.new_branch_location} onChange={(e) => setForm({...form, new_branch_location: e.target.value})} data-testid="transfer-branch" />
            </div>
            <div>
              <Label>New Manager (optional)</Label>
              <Select value={form.new_manager_id} onValueChange={(v) => setForm({...form, new_manager_id: v})}>
                <SelectTrigger data-testid="transfer-manager"><SelectValue placeholder="Select manager" /></SelectTrigger>
                <SelectContent>{managers.slice(0, 50).map(m => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name} — {m.designation}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2 border-t">
            <Switch checked={form.retain_leave_balance} onCheckedChange={(v) => setForm({...form, retain_leave_balance: v})} data-testid="retain-leave-switch" />
            <Label>Retain current-year leave balance (recommended)</Label>
          </div>

          <div>
            <Label>Reason for Transfer *</Label>
            <Input value={form.reason} onChange={(e) => setForm({...form, reason: e.target.value})} placeholder="e.g., Project expansion, Promotion, Restructure" data-testid="transfer-reason" />
          </div>
          <div>
            <Label>Internal Notes (optional)</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} data-testid="transfer-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="confirm-transfer-btn">
            {submitting && <Loader2 size={14} className="animate-spin mr-2" />}<ArrowRightLeft size={14} className="mr-1" />Confirm Transfer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TransferEmployeeDialog;
